<script>
import FileBrowser from '@/components/file-browser/FileBrowser';
import Panel from '@/components/ui/Panel';
import ImageList from '@/components/image-list/ImageList';
import uuid from 'uuid/v4';
import getErrorMessage from '@/modules/server-errors';
import { basename, join, removeBasePath } from '@/modules/file-utilities';
import { prettyFilename } from '@/modules/prettifier';
import URI from 'urijs';

export default {
  name: 'GalleryPanel',
  components: {
    FileBrowser,
    Panel,
    ImageList
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      bookmark: null,
      caption: '',
      description: '',
      editor: {},
      gallery: null,
      images: [],
      isFileBrowserOpen: false,
      isGeneratingThumbnails: false,
      isOneImageSelected: false,
      isUploading: false,
      link: '',
      hasCaptions: true,
      hasLinks: true,
      isList: false,
      selection: [],
      targetDocument: null,
      url: ''
    };
  },
  methods: {
    show(options) {
      const editor = options.editor;
      const element = options.editor ? editor.getSelectedElement() : options.element;
      const gallery = element.closest('.cms-gallery, .cms-editable[data-type="gallery"]');

      this.editor = editor;
      this.gallery = gallery;

      if (editor) {
        // Load settings from the editor
        const root = editor ? editor.getRootElement() : null;
        this.bookmark = editor.getBookmark();
        this.images = gallery ? this.parseImages() : [];
        this.galleryElement = ['div', 'ol', 'ul'].includes(root.getAttribute('data-gallery-element'))
          ? root.getAttribute('data-gallery-element')
          : 'div';
        this.hasCaptions = root.getAttribute('data-gallery-captions') !== 'false';
        this.hasLinks = root.getAttribute('data-gallery-links') !== 'false';
        this.isList = ['ul', 'ol'].includes(this.galleryElement.toLowerCase());
        this.thumbnailWidth = root.getAttribute('data-gallery-thumbnail-width');
        this.thumbnailHeight = root.getAttribute('data-gallery-thumbnail-height');
        this.thumbnailResize = root.getAttribute('data-gallery-thumbnail-resize');
        if (this.thumbnailResize === 'best-fit') this.thumbnailResize = 'fit'; // legacy option
        this.targetDocument = editor.getRootElement().ownerDocument;
      } else {
        // Load settings from a stand-alone gallery
        this.images = this.parseImages();
        this.hasCaptions = gallery ? (gallery.getAttribute('data-captions') === 'false' ? false : true) : false;
        this.hasLinks = gallery ? (gallery.getAttribute('data-links') === 'false' ? false : true) : false;
        this.isList = gallery ? !!gallery.tagName.match(/^(ul|ol)$/i) : false;
        this.thumbnailWidth = gallery ? gallery.getAttribute('data-thumbnail-width') : null;
        this.thumbnailHeight = gallery ? gallery.getAttribute('data-thumbnail-height') : null;
        this.thumbnailResize = gallery ? gallery.getAttribute('data-thumbnail-resize') : null;
        if (this.thumbnailResize === 'best-fit') this.thumbnailResize = 'fit'; // legacy option
        this.targetDocument = gallery.ownerDocument;
      }

      // If width and height are unset, use defaults
      if (!this.thumbnailWidth && !this.thumbnailHeight) {
        this.thumbnailWidth = 256;
        this.thumbnailHeight = 256;
      }

      this.$refs.panel.show();
    },

    hide() {
      this.$refs.panel.hide();
    },

    deleteGallery() {
      if (this.editor) {
        this.$refs.panel.hide();
        this.editor.restoreBookmark(this.bookmark);
        this.editor.removeElement(this.gallery);
      }
    },

    getRelativeUrl(url) {
      const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));

      // Empty URLs gets empty responses
      if (url.trim() === '') {
        return '';
      }

      return new URI(url).relativeTo(pageUrl).toString();
    },

    parseImages() {
      const gallery = this.gallery;
      const images = [];

      if (gallery) {
        [...gallery.querySelectorAll('img')].map(img => {
          const a = img.closest('a');
          images.push({
            id: uuid(),
            src: decodeURI(img.src || ''),
            description: img.alt || '',
            caption: img.getAttribute('data-caption') || '',
            link: decodeURI(a ? a.href || '' : '')
          });
        });
      }

      return images;
    },

    generateGalleryHtml() {
      const images = this.images;
      const hasCaptions = this.hasCaptions;
      const hasLinks = this.hasLinks;
      const isList = this.isList;
      const doc = this.targetDocument;
      const div = doc.createElement('div');

      images.map(image => {
        const src = this.getRelativeUrl(image.src);
        const img = doc.createElement('img');
        let a;
        let li;

        img.src = src;
        img.alt = image.description || '';

        if (hasCaptions) {
          img.setAttribute('data-caption', image.caption || '');
        }

        if (hasLinks) {
          a = doc.createElement('a');
          a.href = this.getRelativeUrl(image.link);
          a.appendChild(img);
        }

        if (isList) {
          li = doc.createElement('li');
          li.appendChild(a || img);
        }

        div.appendChild(li || a || img);
        div.appendChild(doc.createTextNode('\n'));
      });

      return div.innerHTML;
    },

    addImages(type, files) {
      let images = [];
      this.$refs.fileBrowserPopover.doClose();

      // Add images from uploads
      if (type === 'upload') {
        this.isGeneratingThumbnails = true;
        const urls = files.map(file => {
          const path = removeBasePath(file.path, this.site.homeFolder);
          const url = new URI(this.site.url).path(URI.joinPaths(this.site.url, path)).toString();
          return url;
        });

        // Generate thumbnails
        this.$http
          .post(`/sites/${this.site.id}/files/thumbnails`, {
            urls,
            path: join(this.site.uploadFolder, 'thumbnails'),
            width: this.thumbnailWidth,
            height: this.thumbnailHeight,
            resize: this.thumbnailResize
          })
          .then(res => {
            const thumbnails = res.data.thumbnails;
            thumbnails.map(thumbnail => {
              const path = removeBasePath(thumbnail.path, this.site.homeFolder);
              const src = new URI(this.site.url).path(URI.joinPaths(this.site.url, path)).toString();
              images.push({
                id: uuid(),
                src,
                description: prettyFilename(basename(thumbnail.sourceUrl)),
                caption: '',
                link: thumbnail.sourceUrl
              });
            });

            images = images.sort((a, b) => a.link.localeCompare(b.link)); // sort alphabetically
            this.images = this.images.concat(images);
            this.$refs.imageList.setSelection(images);
            this.$nextTick(() => this.$refs.imageList.scrollToSelection());
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => (this.isGeneratingThumbnails = false));
      }

      // Add images from Unsplash
      if (type === 'unsplash') {
        // Unsplash params: https://unsplash.com/documentation#dynamically-resizable-images
        const width = this.thumbnailWidth;
        const height = this.thumbnailHeight;
        const fit = this.thumbnailResize === 'trim' ? 'crop' : 'clip';

        files.map(file => {
          images.push({
            id: uuid(),
            src: `${file.urls.raw}?q=80&auto=format&w=${width}&h=${height}&fit=${fit}`,
            link: file.urls.regular,
            description: file.description,
            caption: `Photo by @${file.user.username} on Unsplash`
          });
        });

        this.images = this.images.concat(images);
        this.$refs.imageList.setSelection(images);
        this.$nextTick(() => this.$refs.imageList.scrollToSelection());
      }
    },

    handleFileDrop(event) {
      const formData = new FormData();
      const files = event.target.files || event.dataTransfer.files;
      const images = [...files].filter(file => file.type.match(/^image\//));

      if (!images.length) return;

      formData.append('path', this.site.uploadFolder);
      images.map(file => formData.append('files', file));
      this.isUploading = true;
      this.$http
        .post(`/sites/${this.site.id}/files`, formData)
        .then(res => this.addImages('upload', res.data.files))
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isUploading = false));
    },

    handleSelect() {
      const selection = this.$refs.imageList.getSelection();
      const image = selection[0];
      this.isOneImageSelected = selection.length === 1;

      if (this.isOneImageSelected) {
        this.caption = image.caption;
        this.description = image.description;
      } else {
        this.caption = '';
        this.description = '';
      }
    },

    handleHide() {
      if (this.editor) {
        this.editor.focus();
      }
    },

    handleShow() {
      if (this.editor) {
        this.editor.blur();
      }
    },

    handleSubmit() {
      this.$refs.panel.hide();

      if (!this.editor) {
        // Update a gallery that's not in an editor
        this.gallery.innerHTML = this.generateGalleryHtml();
      } else if (this.gallery && !this.images.length) {
        // Remove the gallery (no images)
        this.deleteGallery();
      } else if (this.gallery) {
        // Update an existing gallery in the editor
        const html = this.generateGalleryHtml();
        this.editor.restoreBookmark(this.bookmark);
        this.gallery.innerHTML = html;
        this.editor.addUndo();
      } else {
        // Create a new gallery in the editor
        const doc = this.targetDocument;
        const container = doc.createElement(this.galleryElement);
        const html = this.generateGalleryHtml();
        container.classList.add('cms-gallery');
        container.innerHTML = html;
        this.editor.insertContent(container.outerHTML);
      }
    },

    hideFileBrowser() {
      this.$refs.fileBrowser.close();
      this.isFileBrowserOpen = false;
    },

    showFileBrowser() {
      this.isFileBrowserOpen = true;
      this.$refs.fileBrowser.open({
        path: this.site.uploadFolder
      });
    },

    syncImageAttributes() {
      const selection = this.$refs.imageList.getSelection();
      const image = selection[0];
      if (image) {
        image.caption = this.caption;
        image.description = this.description;
      }
    }
  }
};
</script>

<template>
  <panel
    ref="panel"
    :title="$t('Gallery')"
    :close-on-click="!isFileBrowserOpen"
    size="lg"
    @show="handleShow()"
    @hide="handleHide()"
  >
    <el-form label-position="top" novalidate @submit.native.prevent="handleSubmit">
      <div class="image-list-container">
        <!-- Image list -->
        <image-list
          ref="imageList"
          v-model="images"
          :allow-delete="true"
          :allow-drop="true"
          :allow-sort="true"
          :cols="4"
          :empty-text="$t('No Images')"
          :height="280"
          :loading="isUploading || isGeneratingThumbnails"
          :multiple="true"
          @drop="handleFileDrop"
          @select="handleSelect"
        />

        <div class="image-list-controls">
          <el-popover
            ref="fileBrowserPopover"
            placement="bottom"
            trigger="click"
            @show="showFileBrowser()"
            @hide="hideFileBrowser()"
          >
            <el-button slot="reference" :title="$t('Browse')">
              <i class="el-icon-fa-plus" />
            </el-button>

            <file-browser
              ref="fileBrowser"
              :base-path="site.uploadFolder"
              :can-select="file => file.type === 'file' && file.name.match(/\.(gif|jpeg|jpg|png|svg|webp)$/i)"
              :multiple="true"
              :site="site"
              :show-unsplash="true"
              type="popover"
              @select="addImages"
            />
          </el-popover>

          <el-button
            :disabled="$refs.imageList ? $refs.imageList.getSelection().length === 0 : true"
            :title="$t('Delete')"
            @click="$refs.imageList.deleteSelection()"
          >
            <i class="el-icon-fa-trash" />
          </el-button>
        </div>
      </div>

      <fieldset>
        <transition-group name="slide">
          <div v-if="isOneImageSelected" key="controls" class="slide-item">
            <el-row :gutter="20">
              <el-col v-if="hasCaptions" :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <!-- Caption -->
                <el-form-item :label="$t('Caption')">
                  <el-input
                    v-model="caption"
                    :rows="2"
                    :disabled="!isOneImageSelected"
                    type="textarea"
                    auto-complete="off"
                    @input="syncImageAttributes"
                  />
                  <div class="text-form-description">
                    {{ $t('Enter a caption for the selected photo.') }}
                  </div>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <!-- Description -->
                <el-form-item :label="$t('Description')">
                  <el-input
                    v-model="description"
                    :rows="2"
                    :disabled="!isOneImageSelected"
                    type="textarea"
                    auto-complete="off"
                    @input="syncImageAttributes"
                  />
                  <div class="text-form-description">
                    {{ $t("Alternative text for users who can't see images.") }}
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- Actions -->
          <div key="actions" class="slide-item">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-button type="primary" native-type="submit">
                  <span v-if="gallery">{{ $t('Update Gallery') }}</span>
                  <span v-else>{{ $t('Insert Gallery') }}</span>
                </el-button>
              </el-col>
              <el-col v-if="editor" :span="12" class="text-right">
                <el-button v-if="gallery && editor" :title="$t('Delete Gallery')" @click="deleteGallery">
                  <i class="el-icon-fa-trash" />
                </el-button>
              </el-col>
            </el-row>
          </div>
        </transition-group>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.image-list-container {
  position: relative;
  margin-bottom: 1.5rem;

  /deep/ .image-list {
    @media screen and (min-height: 780px) {
      height: 416px !important; // ~3 rows
    }

    @media screen and (min-height: 940px) {
      height: 550px !important; // ~4 rows
    }
  }

  .image-list-controls {
    position: absolute;
    bottom: -1.25rem;
    left: calc(50% - 3.625rem);
    background-color: $--button-default-background-color;
    border: solid 1px $--button-default-border-color;
    border-radius: $--button-border-radius;

    .el-button {
      border: none;
      border-radius: 0;
    }

    > :first-child .el-button {
      border-top-left-radius: $--button-border-radius;
      border-bottom-left-radius: $--button-border-radius;
    }

    > :last-child {
      border-top-right-radius: $--button-border-radius;
      border-bottom-right-radius: $--button-border-radius;
    }
  }
}

.slide-item {
  width: 100%;
  transition: all 0.25s;

  &.slide-enter,
  &.slide-leave-to {
    opacity: 0;
    transform: translateY(-2rem);
  }

  &.slide-leave-active {
    position: absolute;
  }
}
</style>
